import React, { useEffect, useMemo, useState } from 'react';
import { HashRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import { db } from './components/config/firebase.js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, runTransaction } from 'firebase/firestore';
import UserContext from './components/utils/UserContext';

import Header from './components/sectionComponents/header';
import NwuUnescoBanner from './components/sectionComponents/NwuUnescoBanner';
import Footer from './components/sectionComponents/footer';
import ScrollToTop from './components/scrollComponents/ScrollToTop'; // your manual scroll button
import Home from './components/pageComponents/HomePage/Home';
import SearchResultsPage from './components/pageComponents/SearchPage/SearchResultsPage';
import OtherOERsPage from './components/pageComponents/OtherOERsPage/OtherOERsPage';
import SelfDirectedLearningPage from './components/pageComponents/SelfDirectedLearningPage/SelfDirectedLearningPage';
import ContributorsPage from './components/pageComponents/ContributorsPage/ContributorsPage';
import AboutUsPage from './components/pageComponents/AboutUsPage/AboutUsPage';
import FAQPage from './components/pageComponents/FaqPage/FAQPage';
import ContributePage from './components/pageComponents/ContributePage/ContributePage';
import ModerationPage from './components/pageComponents/ModerationPage/ModerationPage';
import GradePage from './components/pageComponents/GradePage/GradePage';
import SignUpPage from './components/pageComponents/SignUpPage/SignUpPage';
import SignInPage from './components/pageComponents/SignInPage/SignInPage';
import RoleAssign from './components/pageComponents/RoleAssign/RoleAssignPage';
import ModeratorAssignmentsPage from './components/pageComponents/ModeratorAssignments/ModeratorAssignmentsPage';
import ViewAccountPage from './components/pageComponents/ViewAccount/ViewAccountPage';
import ForgotPasswordPage from './components/pageComponents/ForgotPasswordPage/ForgotPasswordPage';
import ProtectedRoute from './components/utils/ProtectedRoute';
import GRADES from './components/pageComponents/GradePage/grades.json';
import logger from './components/utils/logger';
import CookieConsent, { getAnalyticsConsent } from './components/sectionComponents/CookieConsent';

// Auto-scroll to top on route change
function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

function App() {
  const [faqOpen, setFaqOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null); // live users/{uid} document
  const [role, setRole] = useState(''); // Store user role
  const [authLoading, setAuthLoading] = useState(true); // true until first auth event resolves
  const [analyticsConsent, setAnalyticsConsent] = useState(getAnalyticsConsent);

  // Google Analytics — only loaded when the user grants consent
  useEffect(() => {
    if (analyticsConsent !== 'granted') return;

    // Avoid injecting the script twice
    if (document.querySelector('script[src*="googletagmanager.com/gtag"]')) return;

    const script = document.createElement('script');
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-SXGLL4M7VW';
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-Z85TC6V1XG');
  }, [analyticsConsent]);

  // Track the signed-in user and keep their Firestore profile live, so role
  // and moderator-scope changes made by an administrator apply immediately.
  //
  // BUG-01: the previous anonymous sign-in fallback ran before Firebase had
  // restored the persisted session. Anonymous auth is disabled on the
  // production project, and where it is enabled it replaces the restored
  // user, so it has been removed. Public pages only read verified resources,
  // which the Firestore rules allow without signing in.
  useEffect(() => {
    const auth = getAuth();
    let unsubscribeProfile = () => {};

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      unsubscribeProfile();
      unsubscribeProfile = () => {};

      if (!user || user.isAnonymous) {
        setCurrentUser(null);
        setProfile(null);
        setRole('');
        setAuthLoading(false);
        return;
      }

      setCurrentUser(user);
      const docRef = doc(db, 'users', user.uid);
      let creatingProfile = false;
      let seenExisting = false;

      unsubscribeProfile = onSnapshot(
        docRef,
        async (docSnap) => {
          if (docSnap.exists()) {
            seenExisting = true;
            const data = docSnap.data();
            setProfile(data);
            setRole(data.role || '');
            setAuthLoading(false);
            return;
          }
          // Only a first-time sign-in gets a new profile. A profile that
          // existed earlier in this session was deleted (account deletion)
          // and must not be recreated.
          if (creatingProfile || seenExisting || docSnap.metadata.hasPendingWrites) {
            setProfile(null);
            setRole('');
            return;
          }
          creatingProfile = true;
          // First-time sign-in with no Firestore profile — create one. A
          // transaction avoids overwriting the profile the sign-up page may
          // be writing at the same moment.
          try {
            await runTransaction(db, async (tx) => {
              const existing = await tx.get(docRef);
              if (existing.exists()) return;
              tx.set(docRef, {
                full_name: user.displayName || null,
                email: user.email || null,
                role: 'open access user',
                requested_role: null,
              });
            });
          } catch (error) {
            logger.error('Error creating user profile:', error);
            setAuthLoading(false);
          }
        },
        (error) => {
          logger.error('Error fetching user profile:', error);
          setProfile(null);
          setRole('');
          setAuthLoading(false);
        }
      );
    });

    return () => {
      unsubscribeProfile();
      unsubscribeAuth();
    };
  }, []);

  const userContext = useMemo(
    () => ({ currentUser, profile, role, authLoading }),
    [currentUser, profile, role, authLoading]
  );

  return (
    <UserContext.Provider value={userContext}>
    <Router>
      <HelmetProvider>
        <div className="App">
          <Header role={role} faqOpen={faqOpen} setFaqOpen={setFaqOpen} />

          {/* Auto scroll to top on route change */}
          <ScrollToTopOnRouteChange />

          <main className="main-content">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/other-oers" element={<OtherOERsPage />} />
              <Route path="/self-directed-learning" element={<SelfDirectedLearningPage />} />
              <Route path="/contributors" element={<ContributorsPage />} />
              <Route path="/contribute" element={
                <ProtectedRoute authLoading={authLoading} role={role} allowedRoles={['educator', 'moderator', 'admin']}>
                  <ContributePage />
                </ProtectedRoute>
              } />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/role-assign" element={
                <ProtectedRoute authLoading={authLoading} role={role} allowedRoles={['admin']}>
                  <RoleAssign />
                </ProtectedRoute>
              } />
              <Route path="/moderator-assignments" element={
                <ProtectedRoute authLoading={authLoading} role={role} allowedRoles={['admin']}>
                  <ModeratorAssignmentsPage />
                </ProtectedRoute>
              } />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/sign-in" element={<SignInPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/moderation" element={
                <ProtectedRoute authLoading={authLoading} role={role} allowedRoles={['moderator', 'admin']}>
                  <ModerationPage />
                </ProtectedRoute>
              } />
              <Route path="/view-account" element={
                <ProtectedRoute authLoading={authLoading} role={role} requireAuth>
                  <ViewAccountPage />
                </ProtectedRoute>
              } />

              {GRADES.map((grade) => (
                <Route
                  key={grade.name}
                  path={`/grade/${grade.name.toLowerCase().replace(/\s+/g, '-')}`}
                  element={<GradePage />}
                />
              ))}
            </Routes>

            <FAQPage isOpen={faqOpen} onClose={() => setFaqOpen(false)} />
          </main>

          {/* Manual scroll to top button */}
          <ScrollToTop />

          <NwuUnescoBanner />
          <Footer />

          <CookieConsent onConsentChange={setAnalyticsConsent} />
        </div>
      </HelmetProvider>
    </Router>
    </UserContext.Provider>
  );
}

export default App;
